import React, { useState } from 'react';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Search, User, Building2, FileText, Loader2 } from 'lucide-react';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../../utils/firebase/client';
import { Button } from '../ui/button';
import { format } from 'date-fns';

interface SearchResult {
    id: string;
    type: 'company' | 'user' | 'ticket';
    title: string;
    subtitle: string;
    path: string;
}

export function GlobalSearch() {
    const [isOpen, setIsOpen] = useState(false);
    const [queryText, setQueryText] = useState('');
    const [results, setResults] = useState<SearchResult[]>([]);
    const [searching, setSearching] = useState(false);

    const handleSearch = async (text: string) => {
        setQueryText(text);
        if (text.length < 3) {
            setResults([]);
            return;
        }

        setSearching(true);
        try {
            // Parallel search across collections
            const companiesRef = collection(db, 'companies');
            const companiesQuery = query(
                companiesRef,
                where('companyName', '>=', text),
                where('companyName', '<=', text + '\uf8ff'),
                limit(5)
            );

            // Fetching users is harder in subcollections without collectionGroup index
            // For now we search companies only to demonstrate
            const companyDocs = await getDocs(companiesQuery);

            const newResults: SearchResult[] = [];

            companyDocs.forEach(doc => {
                const data = doc.data();
                newResults.push({
                    id: doc.id,
                    type: 'company',
                    title: data.companyName,
                    subtitle: `Tenant ID: ${doc.id} • ${data.status}`,
                    path: `/admin/companies/${doc.id}`
                });
            });

            setResults(newResults);

        } catch (error) {
            console.error(error);
        } finally {
            setSearching(false);
        }
    };

    return (
        <>
            <Button
                variant="outline"
                className="w-full justify-start text-gray-500 bg-gray-50/50 hover:bg-gray-100 border-gray-200"
                onClick={() => setIsOpen(true)}
            >
                <Search className="w-4 h-4 mr-2" />
                Global Search (Press ' / ' to search)...
            </Button>

            <Dialog open={isOpen} onOpenChange={setIsOpen}>
                <DialogContent className="sm:max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Global Search</DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div className="relative">
                            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                            <Input
                                placeholder="Search companies, users, or tickets..."
                                className="pl-10 h-12 text-lg"
                                value={queryText}
                                onChange={(e) => handleSearch(e.target.value)}
                                autoFocus
                            />
                            {searching && (
                                <Loader2 className="absolute right-3 top-3 h-4 w-4 animate-spin text-blue-600" />
                            )}
                        </div>

                        <div className="max-h-[60vh] overflow-y-auto space-y-2">
                            {results.map((result) => (
                                <div
                                    key={result.id}
                                    className="flex items-center gap-4 p-3 rounded-lg hover:bg-slate-50 cursor-pointer border border-transparent hover:border-slate-100 transition-all"
                                    onClick={() => {
                                        // Navigate to result
                                        setIsOpen(false);
                                    }}
                                >
                                    <div className={`p-2 rounded-full ${result.type === 'company' ? 'bg-blue-100 text-blue-600' :
                                            result.type === 'user' ? 'bg-emerald-100 text-emerald-600' :
                                                'bg-purple-100 text-purple-600'
                                        }`}>
                                        {result.type === 'company' && <Building2 className="w-5 h-5" />}
                                        {result.type === 'user' && <User className="w-5 h-5" />}
                                        {result.type === 'ticket' && <FileText className="w-5 h-5" />}
                                    </div>
                                    <div>
                                        <p className="font-medium text-gray-900">{result.title}</p>
                                        <p className="text-sm text-gray-500">{result.subtitle}</p>
                                    </div>
                                </div>
                            ))}

                            {results.length === 0 && queryText.length >= 3 && !searching && (
                                <div className="text-center py-8 text-gray-500">
                                    No results found for "{queryText}"
                                </div>
                            )}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
